import { Text } from '@radix-ui/themes'
import { IconInfoCircle } from '@tabler/icons-react'
import { PracticeTestStatus } from '@/types/practice-test'
import Badge from '@/components/ui/badge2'
import Hstack from '@/components/ui/hstack'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Stack from '../stack'

type Props = {
  title?: string
  status?: string
  module?: string
  totalQuestions?: number
  totalMarks?: number
  durationMinutes?: number
  onDurationChange?: (value: number) => void
  readingTimerEnabled?: boolean
  readingDurationSeconds?: number
  onReadingTimerEnabledChange?: (enabled: boolean) => void
  onReadingDurationSecondsChange?: (seconds: number) => void
}

function colorMapping(status: string) {
  if (status === PracticeTestStatus.PUBLISHED) {
    return '!bg-[#ECFDF3] !border-[#ABEFC6] !border !text-[#067647]'
  } else if (status === PracticeTestStatus.DRAFT) {
    return '!text-[#000000] !border !border-[#000000] !bg-[#EAECF0]'
  } else if (status === PracticeTestStatus.INACTIVE) {
    return '!bg-[#FAFAFA] !border-[#E9EAEB] !border !text-[#414651]'
  }
}

function titleTypeMapping(status: string) {
  if (status === PracticeTestStatus.PUBLISHED) {
    return 'Published'
  } else if (status === PracticeTestStatus.DRAFT) {
    return 'Draft'
  } else if (status === PracticeTestStatus.INACTIVE) {
    return 'Deactivated'
  } else {
    return 'N/A'
  }
}

function dotColorMapping(status: string) {
  if (status === PracticeTestStatus.PUBLISHED) {
    return '#17B26A'
  } else if (status === PracticeTestStatus.DRAFT) {
    return '#000000'
  } else if (status === PracticeTestStatus.INACTIVE) {
    return '#414651'
  }
}

export default function TitleBar({
  title,
  status,
  module,
  totalQuestions,
  totalMarks,
  durationMinutes,
  onDurationChange,
  readingTimerEnabled,
  readingDurationSeconds,
  onReadingTimerEnabledChange,
  onReadingDurationSecondsChange,
}: Props) {
  // New practice test (`/create-practice-test/:moduleId`) does not pass `status` until a draft exists.
  // Treat that the same as draft so Duration + Reading Time stay editable.
  const isDraftLike =
    status === PracticeTestStatus.DRAFT ||
    status === undefined ||
    status === null

  // Create/edit draft: only when handlers exist. View (published/inactive): always show like Duration.
  const shouldShowReadingTime = isDraftLike
    ? Boolean(onReadingTimerEnabledChange && onReadingDurationSecondsChange)
    : true

  return (
    <Stack gap={'16px'}>
      <Hstack justify={'between'}>
        <Hstack gap={'16px'} justify={'start'} align={'center'}>
          <Text className='max-w-[840px] truncate text-[40px] !leading-[100%] font-medium'>
            {title}
          </Text>
          {status && (
            <Badge
              title={titleTypeMapping(status) || 'N/A'}
              style={colorMapping(status)}
              dotColor={dotColorMapping(status)}
            />
          )}
        </Hstack>
        {/* {((totalQuestions && totalQuestions > 0) ||
          (totalMarks && totalMarks > 0) ||
          (durationMinutes && durationMinutes > 0)) && ( */}
        <Hstack gap={'16px'} justify={'end'} align={'center'}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                aria-label='Assessment info'
                className='rounded-full p-1 text-[#1D2939] hover:text-[#404040] focus:ring-1 focus:ring-[#98A2B3] focus:ring-offset-2 focus:outline-hidden'
                tabIndex={0}
              >
                <IconInfoCircle />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side='right'
              sideOffset={8}
              hideArrow
              className='max-w-[340px] rounded-[20px] bg-[#D9D9D9] px-4 py-3 text-[12px] text-[#404040] shadow-none'
            >
              <Stack gap={'0px'} className='text-nowrap'>
                <Text>Practice Test has no limit in Time and Marks.</Text>
                <Text>
                  Mock Exam has a 3-hour Time limit and 100 Marks limit.
                </Text>
              </Stack>
            </TooltipContent>
          </Tooltip>
          {/* {totalQuestions && totalQuestions > 0 && ( */}
          <Text className='text-[16px] text-[#1D2939]'>
            Total Questions:{' '}
            <span className='font-medium'>{totalQuestions || 0}</span>
          </Text>
          {/* )} */}
          {/* {durationMinutes && durationMinutes > 0 && ( */}
          <>
            <Text className='text-[16px] text-[#1D2939]'>Duration:</Text>
            {isDraftLike && onDurationChange ? (
              <input
                aria-label='duration in minutes'
                type='number'
                min={0}
                value={
                  Number.isFinite(durationMinutes as number)
                    ? durationMinutes
                    : 0
                }
                onChange={(e) => {
                  const value = e.target.value
                  if (value.length > 1 && value.startsWith('0')) {
                    e.target.value = Number(value).toString()
                  }
                  onDurationChange(Number(e.target.value))
                }}
                className='h-10 w-[160px] rounded-[10px] border border-[#E4E7EC] bg-white px-3 text-center text-[16px] text-[#101828] outline-none'
              />
            ) : (
              <span className='rounded-[10px] border border-[#E4E7EC] bg-white px-4 py-1.5 text-[16px] text-[#101828]'>
                {durationMinutes || 0} minutes
              </span>
            )}
          </>
          {/* )} */}
          {shouldShowReadingTime && (
            <>
              {/* Reading time timer */}
              {isDraftLike &&
              onReadingTimerEnabledChange &&
              onReadingDurationSecondsChange ? (
                <>
                  <Text className='text-[16px] text-[#1D2939]'>
                    Reading Time:
                  </Text>
                  <Hstack gap={'12px'} align={'center'}>
                    <Switch
                      checked={!!readingTimerEnabled}
                      onCheckedChange={(checked) =>
                        onReadingTimerEnabledChange(Boolean(checked))
                      }
                    />
                    <Hstack gap={'4px'} align={'center'}>
                      <input
                        aria-label='reading duration minutes'
                        type='number'
                        min={0}
                        disabled={!readingTimerEnabled}
                        value={
                          Number.isFinite(readingDurationSeconds as number)
                            ? Math.floor((readingDurationSeconds ?? 0) / 60)
                            : 0
                        }
                        onChange={(e) => {
                          const value = e.target.value
                          if (value.length > 1 && value.startsWith('0')) {
                            e.target.value = Number(value).toString()
                          }
                          const mins = Number(e.target.value) || 0
                          const currentSecs =
                            (readingDurationSeconds ?? 0) % 60
                          onReadingDurationSecondsChange(mins * 60 + currentSecs)
                        }}
                        className='h-10 w-[72px] rounded-[10px] border border-[#E4E7EC] bg-white px-2 text-center text-[16px] text-[#101828] outline-none disabled:cursor-not-allowed disabled:opacity-50'
                      />
                      <span className='text-[16px] text-[#1D2939]'>:</span>
                      <input
                        aria-label='reading duration seconds'
                        type='number'
                        min={0}
                        max={59}
                        disabled={!readingTimerEnabled}
                        value={
                          Number.isFinite(readingDurationSeconds as number)
                            ? (readingDurationSeconds ?? 0) % 60
                            : 0
                        }
                        onChange={(e) => {
                          const secs = Math.min(
                            59,
                            Math.max(0, Number(e.target.value) || 0)
                          )
                          const currentMins = Math.floor(
                            (readingDurationSeconds ?? 0) / 60
                          )
                          onReadingDurationSecondsChange(
                            currentMins * 60 + secs
                          )
                        }}
                        className='h-10 w-[72px] rounded-[10px] border border-[#E4E7EC] bg-white px-2 text-center text-[16px] text-[#101828] outline-none disabled:cursor-not-allowed disabled:opacity-50'
                      />
                    </Hstack>
                  </Hstack>
                </>
              ) : (
                <>
                  <Text className='text-[16px] text-[#1D2939]'>
                    Reading Time:
                  </Text>
                  <span className='rounded-[10px] border border-[#E4E7EC] bg-white px-4 py-1.5 text-[16px] text-[#101828]'>
                    {readingTimerEnabled === true
                      ? `${Math.floor((readingDurationSeconds ?? 0) / 60)}:${String((readingDurationSeconds ?? 0) % 60).padStart(2, '0')}`
                      : readingTimerEnabled === false
                        ? 'Disabled'
                        : '—'}
                  </span>
                </>
              )}
            </>
          )}
          {/* {totalMarks && totalMarks > 0 && ( */}
          <Text className='text-[16px] text-[#1D2939]'>
            Total Marks Available:{' '}
            <span className='font-medium'>{totalMarks || 0}</span>
          </Text>
          {/* )} */}
        </Hstack>
        {/* )} */}
      </Hstack>
      {module && (
        <Text className='text-[24px] !leading-[100%] font-medium'>
          {module}
        </Text>
      )}
    </Stack>
  )
}
